import { useCallback, useEffect, useRef, useState } from 'react';
import { citysprintApis } from '../../../apis';
import { mapCitysprintQuoteToParcelQuoteLayout } from '../../../helpers/mapCitysprintQuoteToParcelSections';

const EMPTY_LAYOUT = { vehicleCards: [], serviceCardsByVehicleIndex: [] };

function trimPostcode(pc) {
  return String(pc ?? '').trim();
}

function buildQuoteBody({
  collectionPostcode,
  deliveryPostcode,
  collectionDate,
  timeHour,
  timeMinute,
}) {
  const body = {
    collectionPostcode: trimPostcode(collectionPostcode),
    deliveryPostcode: trimPostcode(deliveryPostcode),
  };

  if (collectionDate?.trim() && timeHour != null && timeMinute != null) {
    const d = new Date(`${collectionDate.trim()}T${timeHour}:${timeMinute}:00`);
    if (!Number.isNaN(d.getTime())) {
      body.collectionDateTime = d.toISOString();
    }
  }

  return body;
}

function getApiErrorMessageFromBody(data) {
  if (typeof data === 'string') {
    return data.trim();
  }
  if (
    data != null &&
    typeof data === 'object' &&
    typeof data.message === 'string'
  ) {
    return data.message.trim();
  }
  return '';
}

function friendlyMessageForHttpStatus(status) {
  switch (status) {
    case 502:
    case 504:
      return 'The service took too long to respond. Please try again in a moment.';
    case 503:
      return 'This service is temporarily unavailable. Please try again shortly.';
    case 500:
    case 501:
      return 'Something went wrong on our side. Please try again in a moment.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 408:
      return 'The request timed out. Please try again.';
    case 404:
      return 'No quotes are available for these details. Check your postcodes and try again.';
    default:
      return null;
  }
}

function friendlyMessageFromAxiosMessage(message) {
  if (!message) {
    return null;
  }
  if (/status code 504|status code 502/i.test(message)) {
    return 'The service took too long to respond. Please try again in a moment.';
  }
  if (/status code 503/i.test(message)) {
    return 'This service is temporarily unavailable. Please try again shortly.';
  }
  if (/status code 50[0-9]/i.test(message)) {
    return 'Something went wrong on our side. Please try again in a moment.';
  }

  if (/status code 40[0-9]/i.test(message)) {
    return 'No quotes are available for these details. Check your postcodes and try again.';
  }

  return null;
}

function mapQuoteFetchError(error) {
  const apiMsg = getApiErrorMessageFromBody(error?.response?.data);
  if (apiMsg && !/^request failed with status code \d+$/i.test(apiMsg)) {
    return apiMsg;
  }

  const fromStatus = friendlyMessageForHttpStatus(error?.response?.status);
  if (fromStatus) {
    return fromStatus;
  }

  const msg = typeof error?.message === 'string' ? error.message : '';
  if (
    !error?.response &&
    (/network error/i.test(msg) || msg === 'Network Error')
  ) {
    return 'Unable to reach the server. Check your connection and try again.';
  }

  const fromAxiosMsg = friendlyMessageFromAxiosMessage(msg);
  if (fromAxiosMsg) {
    return fromAxiosMsg;
  }

  return 'Unable to load quotes. Please try again.';
}

export function useCitysprintParcelQuotes({
  enabled,
  autoFetch = true,
  collectionPostcode,
  deliveryPostcode,
  collectionDate,
  timeHour,
  timeMinute,
}) {
  const [quoteLayout, setQuoteLayout] = useState(EMPTY_LAYOUT);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    const body = buildQuoteBody({
      collectionPostcode,
      deliveryPostcode,
      collectionDate,
      timeHour,
      timeMinute,
    });
    if (!body.collectionPostcode || !body.deliveryPostcode) {
      setQuoteLayout(EMPTY_LAYOUT);
      return { ok: false, errorMessage: null };
    }

    setLoading(true);
    setQuoteLayout(EMPTY_LAYOUT);
    try {
      const response = await citysprintApis.getQuotes(body);
      const payload = response?.data ?? response;
      const mapped = mapCitysprintQuoteToParcelQuoteLayout(payload);
      setQuoteLayout(mapped);
      if (!mapped.vehicleCards?.length) {
        return {
          ok: false,
          errorMessage: 'No quotes returned for these postcodes.',
        };
      }
      return { ok: true, errorMessage: null };
    } catch (e) {
      setQuoteLayout(EMPTY_LAYOUT);
      return { ok: false, errorMessage: mapQuoteFetchError(e) };
    } finally {
      setLoading(false);
    }
  }, [
    collectionDate,
    collectionPostcode,
    deliveryPostcode,
    timeHour,
    timeMinute,
  ]);

  useEffect(() => {
    if (!autoFetch) {
      return;
    }
    if (!enabled) {
      setQuoteLayout(EMPTY_LAYOUT);
      setLoading(false);
      return;
    }
    refetch();
  }, [autoFetch, collectionDate, enabled, refetch, timeHour, timeMinute]);

  const isFirstScheduleEffectRunRef = useRef(true);
  useEffect(() => {
    if (autoFetch) {
      return;
    }
    if (isFirstScheduleEffectRunRef.current) {
      isFirstScheduleEffectRunRef.current = false;
      return;
    }
    setQuoteLayout(EMPTY_LAYOUT);
  }, [autoFetch, collectionDate, timeHour, timeMinute]);

  const refetchForce = useCallback(() => refetch(), [refetch]);

  return { quoteLayout, loading, refetch, refetchForce };
}
